import React from 'react';
import {
    TotalUsers,
    NewUsers,
    TotalOrgnisations,
    ActiveUser1,
} from '../../assets';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';
import Flex from './Flex';
import Typography from './Typographys';
import { TYPOGRAPHY } from '../../constants';

// Register ChartJS components
ChartJS.register(
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

function DashboardStats({ dashboardSummary }) {
    const toNumber = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const monthLabels = (() => {
        const labels = [];
        const now = new Date();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        for (let i = 5; i >= 0; i -= 1) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            labels.push(monthNames[date.getMonth()]);
        }
        return labels;
    })();

    const monthlyIncomeValue = toNumber(dashboardSummary?.monthlyIncome) / 100;
    const annualIncomeValue = toNumber(dashboardSummary?.annualIncome) / 100;
    const activeUsersCount = toNumber(dashboardSummary?.activeUsers);
    const inactiveUsersCount = toNumber(dashboardSummary?.inactiveUsers);
    const formatIndianNumber = (num) => {
        if (num === null || num === undefined) return '0';

        const numStr = num.toString();
        const parts = numStr.split('.');
        let integerPart = parts[0];
        const decimalPart = parts.length > 1 ? '.' + parts[1] : '';

        // Format integer part with commas
        let lastThree = integerPart.substring(integerPart.length - 3);
        let otherNumbers = integerPart.substring(0, integerPart.length - 3);

        if (otherNumbers !== '') {
            lastThree = ',' + lastThree;
        }

        let formatted = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;

        return formatted + decimalPart;
    };
    // Prepare data for charts
    const statsData = [
        {
            value: dashboardSummary?.totalUsers || 0,
            label: 'Total Users',
            icon: TotalUsers,
            bgColor: 'bg-pink-200',
            bgIcon: 'bg-pink-600',
        },
        {
            value: dashboardSummary?.totalOrganizations || 0,
            label: 'Total Organisations',
            icon: TotalOrgnisations,
            bgColor: 'bg-emerald-200',
            bgIcon: 'bg-emerald-600',
        },
        {
            value: dashboardSummary?.newUsers || 0,
            label: 'New Users',
            icon: NewUsers,
            bgColor: 'bg-purple-200',
            bgIcon: 'bg-purple-600',
        },
        {
            value: dashboardSummary?.activeUsers || 0,
            label: 'Active Users',
            icon: ActiveUser1,
            bgColor: 'bg-emerald-200',
            bgIcon: 'bg-emerald-600',
        },
    ];

    // Data for bar chart (User Statistics)
    const barChartData = {
        labels: ['Total Users', 'New Users', 'Active Users', 'Inactive Users'],
        datasets: [
            {
                label: 'User Count',
                data: [
                    dashboardSummary?.totalUsers || 0,
                    dashboardSummary?.newUsers || 0,
                    dashboardSummary?.activeUsers || 0,
                    dashboardSummary?.inactiveUsers || 0,
                ],
                backgroundColor: [
                    'rgba(219, 39, 119, 0.7)',  // pink-600
                    'rgba(147, 51, 234, 0.7)',  // purple-600
                    'rgba(16, 185, 129, 0.7)',  // emerald-600
                    'rgba(234, 88, 12, 0.7)',   // orange-600
                ],
                borderColor: [
                    'rgba(219, 39, 119, 1)',
                    'rgba(147, 51, 234, 1)',
                    'rgba(16, 185, 129, 1)',
                    'rgba(234, 88, 12, 1)',
                ],
                borderWidth: 1,
            },
        ],
    };

    // Data for line chart (Revenue Trends)
    const lineChartPoints = [
        monthlyIncomeValue * 0.8,
        monthlyIncomeValue * 0.9,
        monthlyIncomeValue * 1.1,
        monthlyIncomeValue * 0.95,
        monthlyIncomeValue,
        monthlyIncomeValue * 1.2,
    ].map((value) => (Number.isFinite(value) ? value : 0));

    const lineChartData = {
        labels: monthLabels,
        datasets: [
            {
                label: 'Monthly Revenue',
                data: lineChartPoints,
                backgroundColor: 'rgba(234, 88, 12, 0.2)',
                borderColor: 'rgba(234, 88, 12, 1)',
                borderWidth: 2,
                tension: 0.4,
                fill: true,
            },
        ],
    };

    // Data for pie chart (User Activity)
    const pieChartData = {
        labels: ['Active Users', 'Inactive Users'],
        datasets: [
            {
                data: [
                    activeUsersCount,
                    inactiveUsersCount,
                ],
                backgroundColor: [
                    'rgba(16, 185, 129, 0.7)',  // emerald-600
                    'rgba(234, 88, 12, 0.7)',   // orange-600
                ],
                borderColor: [
                    'rgba(16, 185, 129, 1)',
                    'rgba(234, 88, 12, 1)',
                ],
                borderWidth: 1,
            },
        ],
    };

    const hasRevenueData = lineChartPoints.some((value) => value > 0);
    const hasUserActivityData = activeUsersCount > 0 || inactiveUsersCount > 0;

    // Common chart options
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
            },
            tooltip: {
                enabled: true,
            },
        },
    };

    return (
        <Flex vertical gap={20} className="w-full">
            {/* Quick Stats Cards */}
            <Flex wrap gap={4} className="w-full">
                {statsData.map((stat, index) => (
                    <div
                        key={index}
                        className={`flex flex-col gap-3 justify-start items-start rounded-lg p-4 min-w-[150px] flex-1 ${stat.bgColor}`}
                    >
                        <Flex
                            className={`w-10 h-10 justify-center items-center rounded-full ${stat.bgIcon}`}
                        >
                            {stat.icon && (
                                <img
                                    src={stat?.icon}
                                    alt={stat?.label}
                                    className="object-contain w-6 h-6"
                                />
                            )}
                        </Flex>
                        <Typography
                            text={stat.value}
                            type={TYPOGRAPHY.SUBTITLE_BIG_BLACK}
                            className="!text-2xl"
                        />
                        <Typography text={stat.label} type={TYPOGRAPHY.PARA} />
                    </div>
                ))}
            </Flex>

            {/* Charts Section */}
            <Flex wrap gap={6} className="w-full">
                {/* Bar Chart - User Statistics */}
                <div className="flex-1 min-w-[300px] bg-white p-4 rounded-lg shadow-sm">
                    <Typography
                        text="User Statistics"
                        type={TYPOGRAPHY.SUBTITLE_BIG_BLACK}
                        className="mb-4"
                    />
                    <div className="h-[300px]">
                        <Bar data={barChartData} options={chartOptions} />
                    </div>
                </div>

                {/* Line Chart - Revenue Trends */}
                <div className="flex-1 min-w-[300px] bg-white p-4 rounded-lg shadow-sm">
                    <Typography
                        text="Revenue Trends"
                        type={TYPOGRAPHY.SUBTITLE_BIG_BLACK}
                        className="mb-4"
                    />
                    <div className="h-[300px]">
                        {hasRevenueData ? (
                            <Line data={lineChartData} options={chartOptions} />
                        ) : (
                            <div className="flex h-full items-center justify-center text-sm text-gray-500">
                                No revenue data for the selected period
                            </div>
                        )}
                    </div>
                </div>

                {/* Pie Chart - User Activity */}
                <div className="flex-1 min-w-[300px] bg-white p-4 rounded-lg shadow-sm">
                    <Typography
                        text="User Activity Distribution"
                        type={TYPOGRAPHY.SUBTITLE_BIG_BLACK}
                        className="mb-4"
                    />
                    <div className="h-[300px]">
                        {hasUserActivityData ? (
                            <Pie data={pieChartData} options={chartOptions} />
                        ) : (
                            <div className="flex h-full items-center justify-center text-sm text-gray-500">
                                No user activity data available
                            </div>
                        )}
                    </div>
                </div>
            </Flex>

            {/* Additional Stats Row */}
            <Flex wrap gap={4} className="w-full">
                <div className="flex-1 min-w-[300px] bg-orange-200 rounded-lg p-4">
                    <Typography
                        text={`$${formatIndianNumber(monthlyIncomeValue)}`}
                        type={TYPOGRAPHY.SUBTITLE_BIG_BLACK}
                        className="!text-2xl"
                    />
                    <Typography
                        text="Monthly Recurring Revenue"
                        type={TYPOGRAPHY.PARA}
                    />
                </div>
                <div className="flex-1 min-w-[300px] bg-emerald-200 rounded-lg p-4">
                    <Typography
                        text={`$${formatIndianNumber(annualIncomeValue)}`}
                        type={TYPOGRAPHY.SUBTITLE_BIG_BLACK}
                        className="!text-2xl"
                    />
                    <Typography
                        text="Annual Recurring Revenue"
                        type={TYPOGRAPHY.PARA}
                    />
                </div>
            </Flex>
        </Flex>
    );
}

export default DashboardStats;
